import { AdEnginePlugin, type Hooks } from "../../../../core/AdEnginePlugin";
import type { GptWrapper } from "../../../../core/GptWrapper";
import {
  type AudigentPluginConfig,
  AudigentSegmentKeyvalue,
  type AudigentSegments,
  AudigentTrackingEventName,
  AuSegReadyEvent,
  type PageTargeting,
} from "./types";
import { loadScript, pushTrack } from "utils";
import {
  type FandomCmp,
  LoadTimeEvent,
  type LoadTimeTrackData,
} from "common-types";
import { isConsentGranted } from "../../../../helpers/isConsentGranted";

/** The value to set for the segments when au_seg is empty or undefined */
const DefaultSegmentValue = "-1";
/** Url to the matches script that handles tracking and reporting user data. */
const DEFAULT_MATCHES_SCRIPT_URL = "https://a.ad.gt/api/v1/u/matches/158";
/** Url to the segments script that handles requesting and caching the segments */
const DEFAULT_SEGMENTS_SCRIPT_URL = "https://seg.ad.gt/api/v1/s/158";

/**
 * @todo Document this class including links to audigent documentation, confluence, etc...
 * Documentation will be handled in [COTECH-2231](https://fandom.atlassian.net/browse/COTECH-2231)
 */
export class AudigentPlugin extends AdEnginePlugin {
  /**
   * True when the Audigent script has been added to the page. Ensures we don't install more than
   * once on the page.
   */
  private isInitialized = false;
  /**
   * True when the segments script tag has been added to the page
   */
  private segmentsScriptAdded = false;
  /**
   * True when the segments script tag has been added to the page
   */
  private matchesScriptAdded = false;
  /**
   * Reference to the gptWrapper for updating targeting values
   */
  private gptWrapper?: GptWrapper;
  /**
   * Reference to the current consent
   */
  private consent = false;

  /**
   * Returns if the plugin should be enabled based on the ICBM value of `icAudigent`
   * @param instantConfig The instant config variables to check for icAudigent
   * @returns true if the plugin should be enabled and added to the list of plugins, false if not.
   */
  static isEnabled(instantConfig: AudigentPluginConfig) {
    return !!instantConfig.icAudigent;
  }

  constructor(private readonly instantConfig: AudigentPluginConfig) {
    super();

    // Add the segments script, as this is safe to do pre-consent and doesn't expose any user data
    this.listenForSegmentUpdates();
    this.addSegmentsScript();
  }

  /**
   * Sends a tracking event to the TrackingPlugin for the provided audigent tracking eventName. This
   * will create a {@link Tracking.VENDOR_TIME_UPDATE} event with the eventName provided and set the
   * {@link FandomTrackDataPayload.time} to performance.now()
   */
  private sendTracking(eventName: AudigentTrackingEventName) {
    const trackingPayload: LoadTimeTrackData["payload"] = {
      eventName,
      time: performance.now(),
    };

    pushTrack(LoadTimeEvent.VENDOR_TIME_UPDATE, trackingPayload);
  }

  /**
   * Gets the segments from the window object. If the number of segments exceeds the maximum allowed,
   * then it will drop the additional segments past the max. The max segments is set via the
   * instant config variable {@link AudigentPluginConfig.icAudigentSegmentLimit | icAudigentSegmentLimit}.
   *
   * @returns The audigent segments found or `"-1`" if none are present.
   */
  private getSegments(): AudigentSegments {
    let segments: AudigentSegments = DefaultSegmentValue;

    // get segments from window up to max allowed. If max is not set, then it will use the whole
    // array
    if (window.au_seg) {
      segments = window.au_seg?.segments.slice(
        0,
        this.instantConfig.icAudigentSegmentLimit,
      );
    }

    return segments;
  }

  /**
   * Updates the AU_SEG audience targeting key-value with gptWrapper, applying the latest segments
   * to page keyvalues. This will only add targeting once consent has been given and consent
   * blocking is not true.
   */
  private updateTargeting() {
    // Check if the wrapper has initialized yet or if consent has been granted. If either haven't
    // happened then don't do anything and wait to apply targeting until conditions pass
    if (!this.gptWrapper || !this.consent) {
      return;
    }

    const segments = this.getSegments();
    this.gptWrapper.setTargeting(AudigentSegmentKeyvalue, segments);
  }

  /**
   * Starts a listener to wait for the {@link AuSegReadyEvent} on the document that will be emitted
   * when segments have been added to the window object. When added, we will update the targeting
   * with the latest segments.
   */
  private listenForSegmentUpdates() {
    document.addEventListener(AuSegReadyEvent, () => {
      this.updateTargeting();
      this.sendTracking(AudigentTrackingEventName.SEGMENTS_READY);
    });
  }

  /**
   * Adds the segments script to the page that will retrieve the segments for the user. The segments
   * are embedded in the script itself when retrieved, and once loaded it will make another network
   * call to get updated segments. These all ensure fast retrieval of segments to make the first
   * add call.
   *
   * Since this script send no personal information unless consent had previously been given and
   * information was set in local storage, this is expected and safe to run before consent. It will
   * be installed as early as possible to ensure we make the first ad call with data.
   */
  private async addSegmentsScript() {
    // ensure we only add the script once
    if (this.segmentsScriptAdded) {
      return;
    }

    try {
      await loadScript(DEFAULT_SEGMENTS_SCRIPT_URL);
      this.segmentsScriptAdded = true;
      this.sendTracking(AudigentTrackingEventName.SEGMENTS_SCRIPT_LOADED);
    } catch (error: unknown) {
      console.error(
        `Failed to load Audigent Segments Script ${DEFAULT_SEGMENTS_SCRIPT_URL}`,
        error,
      );
      this.sendTracking(
        AudigentTrackingEventName.SEGMENTS_SCRIPT_ERROR_LOADING,
      );
    }
  }

  /**
   * Adds the matches script that will track the user and send information back to Audigent for them
   * to update the segments for the user. This script will require consent before being loaded and
   * on the page.
   *
   * @returns Resolves when the script tag is loaded on the page.
   */
  private async addMatchesScript() {
    // If we have already added the script, or consent has not been granted, don't add the script.
    if (this.matchesScriptAdded || !this.consent) {
      return;
    }

    try {
      await loadScript(DEFAULT_MATCHES_SCRIPT_URL);
      this.matchesScriptAdded = true;
      this.sendTracking(AudigentTrackingEventName.MATCHES_SCRIPT_LOADED);
    } catch (error: unknown) {
      console.error(
        `Failed to load Audigent Matches Script ${DEFAULT_MATCHES_SCRIPT_URL}`,
        error,
      );
      this.sendTracking(AudigentTrackingEventName.MATCHES_SCRIPT_ERROR_LOADING);
    }
  }

  /**
   * Initializes the script after consent has been given by updating Gpt targeting and installing
   * the matches script that tracks the user.
   */
  private async initialize() {
    if (this.isInitialized) {
      // Already setup, nothing to do here.
      return;
    }

    this.updateTargeting();
    await this.addMatchesScript();

    this.isInitialized = true;
  }

  hooks: Hooks = {
    onConsentUpdated: (fandomCmp: FandomCmp) => {
      this.consent = isConsentGranted(fandomCmp);

      // Only initialize with consent. If we don't have consent this will run once consent is
      // granted on subsequent calls to this hook
      if (this.consent) {
        this.initialize();
      }
    },
    onInitPageTargeting: (initialPageTargeting: PageTargeting) => {
      // Only add targeting with consent.
      if (this.consent) {
        initialPageTargeting.AU_SEG = this.getSegments();
      }
    },
    onAfterGptWrapperInit: (gptWrapper: GptWrapper) => {
      this.gptWrapper = gptWrapper;
      this.updateTargeting();
    },
  };
}
